import { memo, useMemo, useState, useReducer, useEffect } from 'react'
import './GeneralInformationSection.scss'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import { useLocation } from 'react-router-dom'
import { ChartSection } from '../chart-section/ChartSection'
import { GENERAL_INFORMATION, HEIGHT_TEXT } from '../../../../utils/constants'
import generalInformationIcon from '../../../../images/charts/generalInformationIcon.png'
import { getGeneralInfoSectionBodMeasurementsConfig } from '../../../../utils/commonPatientCarts'
import { InfoCard } from '../info-card/InfoCard'
import {
  formatHeightValueForAPI,
  getGenderRow,
  getMaritalRow,
  getMaritalStatusForAPI,
  getRacialForAPI,
  getRacialRow,
  heightRowGenerator,
  inchesToFeetAndInches,
  weightRowGenerator,
} from './tools/tools'
import { EditBodyMeasurementsBlock } from './components/edit-body-measurements-block/EditBodyMeasurementsBlock'
import { EditIdentityBlock } from './components/edit-identity-block/EditIdentityBlock'
import { bodyMeasurementsReducer, setBodyMeasurementsState } from './reducers/BodyMeasurementsReducer'
import { identityReducer, setIdentityReducerState } from './reducers/IdentityReducer'
import { getUserFriendlyErrorMessage, errorCodes } from '../../../../utils/errormessages'
import { setChartsError } from '../../../../store/charts/ChartsActions'
import { updateGeneralInfo } from '../../../../store/charts/ChartsThunk'

const initialBodyMeasurementsState = {
  heightFt: '',
  heightIn: '',
  weightLbs: '',
  heightFtLocalError: '',
  weightLbsLocalError: '',
}

const initialIdentityState = {
  maritalStatus: null,
  sexAtBirth: null,
  genderIdentity: null,
  racialBackground: null,
}

// @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
export const GeneralInformationSection = memo(({ isGeneralInfoEditMode, isMergedAccount = false, userTimeZone }) => {
  const { search } = useLocation()
  const dispatch = useDispatch()
  const params = new URLSearchParams(search)
  const id = params.get('id')

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isGeneralInfoUpdatedSuccess = useSelector((state) => state.chartsReducer.isGeneralInfoUpdatedSuccess)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)
  const currentPatientInfo = patientInfo?.[id]

  const [stateBodyMeasurements, dispatchBodyMeasurements] = useReducer(
    bodyMeasurementsReducer,
    initialBodyMeasurementsState,
  )
  const [stateIdentity, dispatchIdentity] = useReducer(identityReducer, initialIdentityState)

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)

  useEffect(() => {
    if (isGeneralInfoUpdatedSuccess) {
      setIsEditMode(false)
    }
  }, [isGeneralInfoUpdatedSuccess])

  useEffect(() => {
    if (currentPatientInfo) {
      const { feet, inches } = inchesToFeetAndInches(currentPatientInfo?.HeightInches)

      dispatchBodyMeasurements(
        setBodyMeasurementsState({
          heightFt: feet || '',
          heightIn: inches || (feet ? '0' : ''),
          weightLbs: currentPatientInfo?.WeightPounds || '',
        }),
      )

      dispatchIdentity(
        setIdentityReducerState({
          maritalStatus: currentPatientInfo?.MaritalStatus ? getMaritalRow(currentPatientInfo?.MaritalStatus) : '',
          sexAtBirth: currentPatientInfo?.Gender ? getGenderRow(currentPatientInfo?.Gender) : '',
          genderIdentity: currentPatientInfo?.GenderIdentity || '',
          racialBackground:
            currentPatientInfo?.RacialBackground ? getRacialRow(currentPatientInfo?.RacialBackground) : '',
        }),
      )
    }
  }, [currentPatientInfo])

  useEffect(() => {
    if (isGeneralInfoEditMode) {
      setIsEditMode(true)
      setIsCollapsed(false)
    }
  }, [isGeneralInfoEditMode])

  const generalInfoPatientData = useMemo(
    () =>
      getGeneralInfoSectionBodMeasurementsConfig({
        height: heightRowGenerator(currentPatientInfo?.HeightInches),
        maritalStatus: getMaritalRow(currentPatientInfo?.MaritalStatus),
        genderIdentity: currentPatientInfo?.GenderIdentity,
        racialBackGround: getRacialRow(currentPatientInfo?.RacialBackground),
        sexAtBirth: getGenderRow(currentPatientInfo?.Gender),
        weight: weightRowGenerator(currentPatientInfo?.WeightPounds),
      }),
    [
      currentPatientInfo?.Gender,
      currentPatientInfo?.GenderIdentity,
      currentPatientInfo?.HeightInches,
      currentPatientInfo?.MaritalStatus,
      currentPatientInfo?.RacialBackground,
      currentPatientInfo?.WeightPounds,
    ],
  )

  const onClickGeneralInfoButton = () => {
    if (!isEditMode) {
      setIsCollapsed(false)
      setIsEditMode(true)
      return
    }

    const { feet, inches } = inchesToFeetAndInches(currentPatientInfo?.HeightInches)

    const isHeightTheSame =
      (stateBodyMeasurements.heightFt === feet || stateBodyMeasurements.heightFt === '') &&
      (Number(stateBodyMeasurements.heightIn) === inches || stateBodyMeasurements.heightIn === '')

    const isWeightTheSame =
      Number(stateBodyMeasurements.weightLbs) === currentPatientInfo?.WeightPounds ||
      stateBodyMeasurements.weightLbs === ''

    const isGeneralInfoTheSame =
      isHeightTheSame &&
      isWeightTheSame &&
      (stateIdentity.maritalStatus === getMaritalRow(currentPatientInfo?.MaritalStatus) ||
        stateIdentity.maritalStatus === '') &&
      (currentPatientInfo?.GenderIdentity ?
        stateIdentity.genderIdentity === currentPatientInfo?.GenderIdentity
      : stateIdentity.genderIdentity === '') &&
      (stateIdentity.racialBackground === getRacialRow(currentPatientInfo?.RacialBackground) ||
        stateIdentity.racialBackground === '') &&
      (stateIdentity.sexAtBirth === getGenderRow(currentPatientInfo?.Gender) || stateIdentity.sexAtBirth === '')

    if (isGeneralInfoTheSame) {
      dispatchBodyMeasurements(
        setBodyMeasurementsState({
          heightFtLocalError: '',
          weightLbsLocalError: '',
        }),
      )
      setIsEditMode(false)
    } else {
      if (stateBodyMeasurements.heightIn && !stateBodyMeasurements.heightFt) {
        dispatchBodyMeasurements(
          setBodyMeasurementsState({
            heightFtLocalError: getUserFriendlyErrorMessage(
              errorCodes.ENTER_VALID,
              null,
              HEIGHT_TEXT.toLocaleLowerCase(),
            ),
          }),
        )
        dispatch(setChartsError(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)))
        return
      }

      const newGeneralInfoData = {
        HeightInches:
          !isHeightTheSame ?
            formatHeightValueForAPI(stateBodyMeasurements.heightFt, stateBodyMeasurements.heightIn)
          : null,
        WeightPounds: !isWeightTheSame ? Number(stateBodyMeasurements.weightLbs) : null,
        MaritalStatus: getMaritalStatusForAPI(stateIdentity.maritalStatus),
        GenderIdentity: stateIdentity.genderIdentity,
        RacialBackground: getRacialForAPI(stateIdentity.racialBackground),
        Gender: stateIdentity.sexAtBirth,
      }

      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(updateGeneralInfo(id, newGeneralInfoData))
    }
  }

  return (
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    <ChartSection
      id='general-info-section'
      className={classNames('general-information-block-patient-chart', { opened: !isCollapsed })}
      title={GENERAL_INFORMATION}
      icon={generalInformationIcon}
      iconAltText='alerts'
      setIsCollapsed={setIsCollapsed}
      clickButtonAction={onClickGeneralInfoButton}
      isCollapsed={isCollapsed}
      isEditMode={isEditMode}
      userTimeZone={userTimeZone}
      isWithoutLastUpdated
      isShowEmptyContent
      isShouldScrollUpContent
      isShowButton={!isMergedAccount}>
      {
        isEditMode ?
          <>
            <EditBodyMeasurementsBlock
              // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
              stateInfo={stateBodyMeasurements}
              dispatchInfo={dispatchBodyMeasurements}
              currentPatientInfo={currentPatientInfo}
            />
            {/*
           // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
            <EditIdentityBlock stateInfo={stateIdentity} dispatchInfo={dispatchIdentity} />
          </>
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        : generalInfoPatientData.map((infoCardItemConfig) => <InfoCard infoConfig={infoCardItemConfig} />)
      }
    </ChartSection>
  )
})
